import { FieldVisitActions } from "@/components/common/section-components/action/FieldVisitActions";
import { NoData } from "@/components/common/section-components/NoData";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { FieldVisitFilter } from "@/components/common/section-components/filters/FieldVisitFilter";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { StandardTable } from "@/components/table/StandardTable";
import { PermissionCode } from "@/constants/permissions";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { Flex } from "@chakra-ui/react";
import dayjs from "dayjs";

import { isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { FieldVisitListItem, FieldVisitListResponse } from "./schema/field-visit.schema";

import {
  clearFiltersHandler,
  fetchFieldVisitsHandler,
  submitFiltersHandler,
} from "./schema/handler-functions";
import { fieldVisitHeaderElements } from "./schema/header-elements";
import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { FieldVisitStatus } from "@/constants/entity-status";
import { EstadosVisitaCampo } from "@/constants/enums/status/entity-status-ids";
import { usePolling } from "@/hooks/usePolling";

export const FieldVisitPage = () => {

  const [isActive, setIsActive] = useState<boolean>(false)

  useEffect(() => {
    setIsActive(true)
  }, [])
  const navigate = useNavigate();
  const { fetchPermissions, userPermissions } = useRoles();

  const hasWriteAccess = Boolean(
    userPermissions?.some(
      (p) => p.codigo === PermissionCode.FIELD_VISIT && p.tipoPermiso === "W",
    ),
  );

  const { control, handleSubmit, setValue } = useForm();
  const values = useWatch({ control });
  const filtersEmpty = useMemo(
    () => Object.values(values ?? {}).every((v) => isEmpty(v)),
    [values],
  );

  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [list, setList] = useState<FieldVisitListResponse>();
  const [currentFilters, setCurrentFilters] = useState<any>();
  const [page, setPage] = useState<number>(1);

  const fetchList = async (filters?: any, showLoad = true) => {
    await fetchFieldVisitsHandler({
      filters,
      showLoad,
      setLoading,
      setList,
      navigate,
      fetchPermissions,
    });
  };

  useEffect(() => {
    if (!isActive) return;

    const activeStates = [
      EstadosVisitaCampo.PendienteAsignacion,
      EstadosVisitaCampo.Asignada,
      EstadosVisitaCampo.Realizada,
    ];

    setValue(
      "Estados",
      activeStates.map(String),
    );

    const nextFilters = {
      Estados: activeStates,
      "P.Pagina": 1,
    };

    setCurrentFilters(nextFilters);
    setFiltersApplied(true);
    setPage(1);

    fetchList(nextFilters);
  }, [isActive]);

  usePolling({
    callback: () => {
     if (!loading) fetchList(currentFilters, false)
    },
    isActive,
    delay: 30000,
  });

  const submitFilters = async (event: FieldValues) => {
    await submitFiltersHandler({
      event,
      setPage,
      fetchList,
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
      filtersEmpty,
    });
  };

  const clearFilters = async () => {
    await clearFiltersHandler({
      setValue,
      setFiltersApplied,
      setOpenFilters,
      setPage,
      setCurrentFilters,
      fetchList,
    });
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchList({ ...currentFilters, "P.Pagina": nextPage });
  };

  const handleDetail = (item: FieldVisitListItem) => {
    navigate(AppRoutes.FIELD_VISIT_DETAIL.replace(":id", String(item.id)));
  };

  const getActionElements = (item: FieldVisitListItem) => (
    <FieldVisitActions handleDetail={handleDetail} item={item} />
  );

  const handleAdd = () => {
    navigate(AppRoutes.FIELD_VISIT_CREATION);
  };

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px" data-testid="field-visit-page">
      <SectionHeader
        title="Visita a campo"
        loading={loading}
        addButtonLabel="Agregar"
        handleAdd={handleAdd}
        showButtons={hasWriteAccess}
      />

      <FieldVisitFilter
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        submitFilters={submitFilters}
        clearFilters={clearFilters}
        control={control}
        handleSubmit={handleSubmit}
        filtersApplied={filtersApplied}
        disableFilters={loading}
        loadingFilters={loading}
        disableApply={filtersEmpty && !filtersApplied}
      />

      <Flex flexDir="column">
        {Boolean(!loading && !list?.listado?.length) && <NoData />}

        {Boolean(!loading && list?.listado?.length) && (
          <>
            <StandardTable
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Ver"
              noSelection
              items={
                list?.listado?.map((el: FieldVisitListItem) => ({
                  ...el,
                  fechaDeseada: el?.fechaDeseada
                    ? dayjs(el.fechaDeseada).format("DD/MM/YYYY")
                    : "-",
                  displayEstado: (
                    <RequestStatusBadge status={el?.estado as FieldVisitStatus} />
                  ),
                })) ?? []
              }
              headers={fieldVisitHeaderElements}
              actionElements={getActionElements}
            />

            {Number(list?.totalPages) > 1 && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={list?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};